import { createFileRoute, redirect } from '@tanstack/react-router'
import { Building2, Calendar, Settings, Users } from 'lucide-react'
import { useMemo, useState } from 'react'
import Page from '~/components/Page'
import { DashboardDateRangePicker } from '~/components/routes/visioReviews/DashboardDateRangePicker'
import { FilterSelect } from '~/components/routes/visioReviews/FilterSelect'
import { InteractionsTab } from '~/components/routes/visioReviews/InteractionsTab'
import { OperatorsTab } from '~/components/routes/visioReviews/OperatorsTab'
import { StoresTab } from '~/components/routes/visioReviews/StoresTab'
import {
  DEFAULT_INTERACTION_TYPE_FILTER,
  type DateRangePreset,
  getDateRangeForPreset,
  getPreviousPeriodForPreset,
  INTERACTION_TYPE_OPTIONS,
} from '~/components/routes/visioReviews/visioReviews.utils'
import { Button } from '~/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'

const isProduction =
  import.meta.env.VITE_BACKEND_API_ENDPOINT?.includes('api.lucie-la-cabane-autonome.fr') &&
  !import.meta.env.VITE_BACKEND_API_ENDPOINT?.includes('staging')

export const Route = createFileRoute('/_authenticated/visio-reviews/')({
  beforeLoad: () => {
    if (isProduction) throw redirect({ to: '/dashboard' })
  },
  component: VisioReviewsPage,
})

function VisioReviewsPage() {
  const navigate = Route.useNavigate()
  const [interactionTypeFilter, setInteractionTypeFilter] = useState(
    DEFAULT_INTERACTION_TYPE_FILTER,
  )
  const [preset, setPreset] = useState<DateRangePreset | null>('30d')
  const [dateRange, setDateRange] = useState(() => getDateRangeForPreset('30d'))
  // "Veille" uses a hardcoded yesterday range, so its date picker is hidden.
  const [activeTab, setActiveTab] = useState('veille')

  // Previous period is null for "Toujours" → StoresTab / OperatorsTab hide the
  // trend column and omit previousStart/previousEnd from their queries.
  const previousPeriod = useMemo(
    () => (preset ? getPreviousPeriodForPreset(preset, dateRange) : null),
    [preset, dateRange],
  )

  return (
    <Page>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Contrôle Qualité des Interventions</h1>
          <Button variant="outline" onClick={() => navigate({ to: '/visio-reviews/admin' })}>
            <Settings className="h-4 w-4" />
            Administration
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <TabsList>
              <TabsTrigger value="veille" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Interactions de la Veille
              </TabsTrigger>
              <TabsTrigger value="boutique" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Par Boutique
              </TabsTrigger>
              <TabsTrigger value="operateur" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Par Opérateur
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-3">
              {activeTab !== 'veille' && (
                <DashboardDateRangePicker
                  value={dateRange}
                  preset={preset}
                  onChange={(range, newPreset) => {
                    setDateRange(range)
                    setPreset(newPreset)
                  }}
                />
              )}
              <FilterSelect
                label="Type d'interaction"
                value={interactionTypeFilter}
                onChange={setInteractionTypeFilter}
                options={INTERACTION_TYPE_OPTIONS}
                width="w-48"
              />
            </div>
          </div>

          <TabsContent value="veille" className="mt-6">
            <InteractionsTab interactionTypes={interactionTypeFilter} />
          </TabsContent>

          <TabsContent value="boutique" className="mt-6">
            <StoresTab
              interactionTypes={interactionTypeFilter}
              dateRange={dateRange}
              previousPeriod={previousPeriod}
            />
          </TabsContent>

          <TabsContent value="operateur" className="mt-6">
            <OperatorsTab
              interactionTypes={interactionTypeFilter}
              dateRange={dateRange}
              previousPeriod={previousPeriod}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Page>
  )
}
